/**
 * BlockRenderer — dispatches UI blocks to their respective components.
 * Groups consecutive metric_card blocks and keyword_chips blocks for grid layout.
 * Non-text blocks stagger in with a fade+slide animation for a streaming feel.
 */

import type { UIBlock, MetricCardBlock, KeywordChipsBlock, ContentSuggestionBlock } from '../../stores/CoPilotStore'
import { MetricCardGroup } from './blocks/MetricCardGroup'
import { DataTable } from './blocks/DataTable'
import { ActionList } from './blocks/ActionList'
import { TextBlock } from './blocks/TextBlock'
import { AlertBanner } from './blocks/AlertBanner'
import { KeywordChips } from './blocks/KeywordChips'
import { ContentSuggestion } from './blocks/ContentSuggestion'

type GroupedItem = UIBlock | MetricCardBlock[] | KeywordChipsBlock[] | ContentSuggestionBlock[]

/** Group consecutive same-type blocks into arrays for grid rendering */
function groupBlocks(blocks: UIBlock[]): GroupedItem[] {
  const result: GroupedItem[] = []
  let metricGroup: MetricCardBlock[] = []
  let chipsGroup: KeywordChipsBlock[] = []
  let contentGroup: ContentSuggestionBlock[] = []

  function flushMetrics() {
    if (metricGroup.length > 0) { result.push(metricGroup); metricGroup = [] }
  }
  function flushChips() {
    if (chipsGroup.length > 0) { result.push(chipsGroup); chipsGroup = [] }
  }
  function flushContent() {
    if (contentGroup.length > 0) { result.push(contentGroup); contentGroup = [] }
  }
  function flushAll() { flushMetrics(); flushChips(); flushContent() }

  for (const block of blocks) {
    if (block.type === 'metric_card') {
      flushChips(); flushContent()
      metricGroup.push(block)
    } else if (block.type === 'keyword_chips') {
      flushMetrics(); flushContent()
      chipsGroup.push(block)
    } else if (block.type === 'content_suggestion') {
      flushMetrics(); flushChips()
      contentGroup.push(block as ContentSuggestionBlock)
    } else {
      flushAll()
      result.push(block)
    }
  }
  flushAll()
  return result
}

/** Check if a grouped item is a KeywordChipsBlock array */
function isChipsGroup(item: GroupedItem): item is KeywordChipsBlock[] {
  return Array.isArray(item) && item.length > 0 && item[0].type === 'keyword_chips'
}

/** Check if a grouped item is a MetricCardBlock array */
function isMetricGroup(item: GroupedItem): item is MetricCardBlock[] {
  return Array.isArray(item) && item.length > 0 && item[0].type === 'metric_card'
}

/** Check if a grouped item is a ContentSuggestionBlock array */
function isContentGroup(item: GroupedItem): item is ContentSuggestionBlock[] {
  return Array.isArray(item) && item.length > 0 && item[0].type === 'content_suggestion'
}

export function BlockRenderer({ blocks }: { blocks: UIBlock[] }) {
  const grouped = groupBlocks(blocks)

  // Track non-text block index for stagger delay
  let nonTextIdx = 0

  return (
    <div className="space-y-3">
      {grouped.map((item, i) => {
        // Text blocks render inline — no animation (they stream in naturally)
        if (!Array.isArray(item) && item.type === 'text') {
          return <TextBlock key={`tx-${i}`} block={item} />
        }

        // Non-text blocks: stagger fade-in animation
        const delay = nonTextIdx * 150 + 100 // 100ms initial delay, 150ms between each
        nonTextIdx++

        const staggerStyle: React.CSSProperties = {
          opacity: 0,
          animation: `fadeSlideIn 0.35s ease-out ${delay}ms forwards`,
        }

        // Grouped metric cards
        if (isMetricGroup(item)) {
          return (
            <div key={`group-${i}`} style={staggerStyle}>
              <MetricCardGroup cards={item} />
            </div>
          )
        }

        // Grouped content suggestion cards → responsive grid
        if (isContentGroup(item)) {
          return (
            <div key={`content-group-${i}`} style={staggerStyle}>
              <div className="space-y-2">
                <div className="text-[14px] font-semibold text-[#18181B]">
                  Content Suggestions
                </div>
                <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3">
                  {item.map((block) => (
                    <ContentSuggestion key={`${block.asin}-${block.sku}`} block={block} />
                  ))}
                </div>
              </div>
            </div>
          )
        }

        // Grouped keyword chips → responsive grid
        if (isChipsGroup(item)) {
          return (
            <div key={`chips-group-${i}`} style={staggerStyle}>
              <div className="space-y-2">
                <div className="text-[14px] font-semibold text-[#18181B]">
                  Keyword Suggestions
                </div>
                <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3">
                  {item.map((block) => (
                    <KeywordChips key={`${block.asin}-${block.sku}`} block={block} />
                  ))}
                </div>
              </div>
            </div>
          )
        }

        // Single blocks
        if (!Array.isArray(item)) {
          switch (item.type) {
            case 'metric_card':
              return (
                <div key={`mc-${i}`} style={staggerStyle}>
                  <MetricCardGroup cards={[item]} />
                </div>
              )
            case 'data_table':
              return (
                <div key={`dt-${i}`} style={staggerStyle}>
                  <DataTable block={item} />
                </div>
              )
            case 'action_list':
              return (
                <div key={`al-${i}`} style={staggerStyle}>
                  <ActionList block={item} />
                </div>
              )
            case 'alert':
              return (
                <div key={`ab-${i}`} style={staggerStyle}>
                  <AlertBanner block={item} />
                </div>
              )
            case 'keyword_chips':
              return (
                <div key={`kc-${i}`} style={staggerStyle}>
                  <KeywordChips block={item} />
                </div>
              )
            case 'content_suggestion':
              return (
                <div key={`cs-${i}`} style={staggerStyle}>
                  <ContentSuggestion block={item} />
                </div>
              )
            default:
              return null
          }
        }

        return null
      })}
    </div>
  )
}
